import type { ListExperimentQueryParams } from "@globals";
import type { DataTableSortStatus } from "mantine-datatable";
import { useCallback, useEffect, useState } from "react";

export type PaginationQueryParams = Pick<
  NonNullable<ListExperimentQueryParams>,
  "limit" | "offset" | "sort_field" | "sort_order"
>;

export interface UseTableSortingPaginationProps<T> {
  pageSize?: number;
  initialSort?: {
    columnAccessor: keyof T;
    direction: "asc" | "desc";
  };
  sortFieldMap?: Record<string, string>;
  onQueryChange?: (queryParams: PaginationQueryParams) => void;
  totalCount?: number;
}

export interface UseTableSortingPaginationReturn<T> {
  currentPage: number;
  setCurrentPage: (page: number) => void;
  sortStatus: DataTableSortStatus<T>;
  setSortStatus: (status: DataTableSortStatus<T>) => void;
  totalPages: number;
}

export function useTableSortingPagination<T>({
  pageSize = 20,
  initialSort = { columnAccessor: "created_at" as keyof T, direction: "desc" },
  sortFieldMap = {},
  onQueryChange,
  totalCount = 0
}: UseTableSortingPaginationProps<T>): UseTableSortingPaginationReturn<T> {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<T>>({
    columnAccessor: initialSort.columnAccessor,
    direction: initialSort.direction
  });

  const totalPages = Math.ceil(totalCount / pageSize);

  // Map frontend field names to backend field names
  const mapSortField = useCallback(
    (field: string) => {
      return sortFieldMap[field] || field;
    },
    [sortFieldMap]
  );

  // Call onQueryChange when pagination or sorting changes
  useEffect(() => {
    if (!onQueryChange) {
      return;
    }

    const queryParams = {
      limit: pageSize,
      offset: (currentPage - 1) * pageSize,
      sort_field: mapSortField(String(sortStatus.columnAccessor)),
      sort_order: sortStatus.direction
    };

    onQueryChange(queryParams);
  }, [pageSize, currentPage, sortStatus.columnAccessor, sortStatus.direction, mapSortField, onQueryChange]);

  return {
    currentPage,
    setCurrentPage,
    sortStatus,
    setSortStatus,
    totalPages
  };
}
